import { useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { loadPage } from '../lib/page'
import { htmlToMarkdown } from '../lib/markdown'
import { saveFile } from '../lib/download'
import { SITE } from '../lib/seo'
import { docPath, type DocSummary } from '../lib/types'
import { Button } from './ui'

/** Storage tolerates far more, but a stampede of parallel fetches helps nobody. */
const CONCURRENCY = 6

const today = () => new Date().toISOString().slice(0, 10)

interface Exported extends DocSummary {
  /** `null` when the body could not be read — the export lists it rather than lying. */
  content: string | null
}

/** Run `fn` over `items`, at most `limit` in flight, preserving order. */
async function pooled<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length)
  let next = 0

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length) {
        const i = next++
        results[i] = await fn(items[i]!)
      }
    }),
  )
  return results
}

async function fetchAll(onProgress: (done: number, total: number) => void): Promise<Exported[]> {
  const summaries = await api.docs()
  let done = 0

  return pooled(summaries, CONCURRENCY, async (summary) => {
    let content: string | null = null
    try {
      const detail = await api.doc(summary.id)
      const page = await loadPage(detail.page)
      content = htmlToMarkdown(page.html)
    } catch {
      // Storage rules or the API said no. Skip the body, keep the entry.
      content = null
    }
    onProgress(++done, summaries.length)
    return { ...summary, content }
  })
}

function toMarkdown(docs: Exported[]): string {
  const out = [
    '# SightHub documentation',
    '',
    `Generated ${today()} from ${SITE}. ${docs.length} document${docs.length === 1 ? '' : 's'}.`,
    'Entries marked **internal** are staff-only — do not paste them anywhere public.',
    '',
  ]

  for (const d of docs) {
    out.push('---', '')
    out.push(`## ${d.title}`, '')
    out.push(`- Source: ${SITE}${docPath(d)}`)
    out.push(`- Path: \`${d.service}/${d.topic}/${d.slug}\``)
    out.push(`- Visibility: ${d.visibility === 'internal' ? '**internal**' : 'public'}`)
    if (d.tags.length) out.push(`- Tags: ${d.tags.map((t) => `\`${t}\``).join(', ')}`)
    out.push(`- Updated: ${d.updated_at}`)
    out.push('')
    if (d.description) out.push(`> ${d.description}`, '')
    out.push(d.content ?? '_Body unavailable: you do not have access to this document._', '')
  }

  return out.join('\n')
}

/**
 * Download the documentation the caller can read — metadata *and* page bodies,
 * as one file meant to be dropped into an assistant's context.
 *
 * Everything is fetched with the signed-in user's credentials, so the export can
 * never contain more than the reader is entitled to: internal bodies come back
 * empty for non-staff, enforced by Storage rules rather than by this component.
 */
export function ExportDocs() {
  const { staff } = useAuth()
  const [busy, setBusy] = useState<'json' | 'md' | null>(null)
  const [progress, setProgress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!staff) return null

  const download = async (format: 'json' | 'md') => {
    setBusy(format)
    setError(null)
    try {
      const docs = await fetchAll((done, total) => setProgress(`${done}/${total}`))

      if (format === 'json') {
        const payload = { generated_at: new Date().toISOString(), site: SITE, count: docs.length, docs }
        saveFile(`sighthub-docs-${today()}.json`, JSON.stringify(payload, null, 2), 'application/json')
      } else {
        saveFile(`sighthub-docs-${today()}.md`, toMarkdown(docs), 'text/markdown')
      }

      const missing = docs.filter((d) => d.content === null).length
      if (missing) setError(`${missing} document(s) exported without a body — no read access.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed.')
    } finally {
      setBusy(null)
      setProgress(null)
    }
  }

  return (
    <div className="rounded-[var(--radius-sh)] border border-line bg-surface p-5 shadow-card">
      <h2 className="font-semibold text-ink">Export the documentation</h2>
      <p className="mt-1 text-sm text-muted">
        Every document you can read, bodies included, as one file — made to hand to an
        assistant in your IDE. Internal docs are in there only if your role allows them.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="primary" size="sm" disabled={busy !== null} onClick={() => void download('md')}>
          {busy === 'md' ? `Fetching ${progress ?? ''}` : 'Download Markdown'}
        </Button>
        <Button size="sm" disabled={busy !== null} onClick={() => void download('json')}>
          {busy === 'json' ? `Fetching ${progress ?? ''}` : 'Download JSON'}
        </Button>
      </div>

      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
    </div>
  )
}
